import { Workflow } from 'src/redux/consts'
import { AsyncTask } from 'src/redux/slices/async-processing/consts'
import { addAsyncExtraReducers, TaggedHandlers } from 'src/redux/slices/async-processing/utils'
import { addErrorExtraReducers } from 'src/redux/slices/error/utils'
import { ContextLayers, Entrypoint, KeyField, KeyFields } from 'src/redux/slices/umibuilder/consts'
import {
  Archetype,
  ArchetypeGroup,
  BaseGisData,
  ContextLayerConfiguration,
  KeyFieldConfiguration,
  Summary,
  TemplatLibraryFilters,
  UmibuilderState,
} from 'src/redux/slices/umibuilder/types'
import { addErrorsToGeoJSON } from 'src/redux/slices/umibuilder/utils'
import { getEntries } from 'src/redux/utils'

import { createSlice, current, PayloadAction } from '@reduxjs/toolkit'

const workflow = Workflow.umibuilder as keyof typeof Workflow

const makeKeyFields = (): KeyFieldConfiguration => {
  const keyFields = {} as KeyFieldConfiguration
  KeyFields.forEach((field) => (keyFields[field] = null))
  return keyFields
}

const makeContextLayers = (): ContextLayerConfiguration => {
  const contextLayers = {} as ContextLayerConfiguration
  ContextLayers.forEach((layer) => (contextLayers[layer] = false))
  return contextLayers
}

const makeBaseGisData = (): BaseGisData => ({
  geoJSON: null, // Frontend Mutable via Interaction
  largeGeoJSON: false,
  categoricalProps: null,
  numericalProps: null,
  shapefileProps: null,
  mapBounds: null,
  climateZones: null,
})

// TODO: when to clear fromCloud?
const initialState: UmibuilderState = {
  entrypoint: Entrypoint.gis,
  fromCloud: false,

  // Processed Base Data
  ...makeBaseGisData(),

  // KeyField Assignments
  keyFields: makeKeyFields(), // Frontend Mutable via Interaction
  keyFieldsErrorSummary: {
    // Frontend Mutable via Interaction
    any: 0,
  },

  // Group Data
  groups: null, // Frontend Mutable via Interaction

  // Summary Data
  summary: null,

  // Context Layer
  contextLayers: makeContextLayers(), // Frontend Mutable via Interaction

  // Template Library Filter // Frontend Mutable via Interaction
  libraryFilters: {
    ClimateZone: [],
    Type: [],
  },

  // Umi Export
  umiReady: false,

  // Other
  isProcessing: false,

  error: null,
  errors: {},
  runtimeError: undefined,
}

const umibuilderSlice = createSlice({
  name: workflow,
  initialState,
  reducers: {
    setEntrypoint: (state, { payload }: PayloadAction<Pick<UmibuilderState, 'entrypoint'>>) => {
      state.entrypoint = payload.entrypoint
    },
    setCloudContext: (state, { payload }: PayloadAction<boolean>) => {
      state.fromCloud = payload
    },
    setIsProcessing: (state, { payload }: PayloadAction<boolean>) => {
      state.isProcessing = payload
    },
    setAllKeyFields: (state, { payload }: PayloadAction<KeyFieldConfiguration>) => {
      state.keyFields = {
        ...payload,
      }
      handleClearGroupData(state)
    },
    loadGeoJSON: (
      state,
      { payload }: PayloadAction<Pick<UmibuilderState, 'geoJSON' | 'largeGeoJSON'>>,
    ) => {
      const { geoJSON, largeGeoJSON } = payload
      // TODO: why do we have to copy here? probably because it is a deep object coming fom RTK query?
      // Probably better to load into redux in the onQueryStarted handler...
      state.geoJSON = JSON.parse(JSON.stringify(geoJSON))
      state.largeGeoJSON = largeGeoJSON
      addInitialErrorsToGeoJSON(state)
      state.keyFieldsErrorSummary = addErrorsToGeoJSON({
        geoJSON: state.geoJSON,
        assignments: state.keyFields,
      }).errorSummary
    },
    computeErrors: (state) => {
      state.keyFieldsErrorSummary = addErrorsToGeoJSON({
        geoJSON: state.geoJSON,
        assignments: state.keyFields,
      }).errorSummary
    },
    setKeyFieldAssignment: (
      state,
      { payload }: PayloadAction<{ field: keyof typeof KeyField; value: string }>,
    ) => {
      handleClearGroupData(state)
      state.keyFields[payload.field] = payload.value
      state.keyFieldsErrorSummary = addErrorsToGeoJSON({
        geoJSON: state.geoJSON,
        assignments: state.keyFields,
      }).errorSummary
    },
    loadGroupData: (state, { payload }) => {
      state.groups = payload
    },
    addSubdivision: (
      state,
      { payload }: PayloadAction<{ field: string | null; breakpoint: number }>,
    ) => {
      handleClearSummaryData(state)
      const { field, breakpoint } = payload
      const updatePrimaryGroup = (primaryGroup: Archetype) => {
        const { groups } = primaryGroup
        const groupToSplit = groups.find(
          (group) => group.secondaryMin <= breakpoint && group.secondaryMax >= breakpoint,
        )
        if (groupToSplit) {
          const { secondaryMax, primaryField, secondaryMin } = groupToSplit
          const newGroup = {
            ...groupToSplit,
          }
          groupToSplit.secondaryMax = breakpoint
          groupToSplit.archetypeID = `${primaryField}_${secondaryMin}_to_${breakpoint}`
          newGroup.secondaryMin = breakpoint
          newGroup.secondaryMax = secondaryMax
          newGroup.archetypeID = `${primaryField}_${breakpoint}_to_${secondaryMax}`
          groups.push(newGroup)
          groups.sort((a, b) => a.secondaryMin - b.secondaryMin)
        }
      }

      if (field != null) {
        const primaryGroup = state.groups?.find(({ field: _field }) => field === _field)
        // TODO: should throw errors if not found
        if (!primaryGroup) return
        updatePrimaryGroup(primaryGroup)
      } else {
        state.groups?.forEach(updatePrimaryGroup)
      }
    },
    removeSubdivision: (state, { payload }: PayloadAction<{ field: string; index: number }>) => {
      handleClearSummaryData(state)
      const { field, index } = payload
      const primaryGroup = state.groups?.find(({ field: _field }) => field === _field)
      // TODO: should throw errors if not found
      if (!primaryGroup) return
      const { groups } = primaryGroup
      const groupToPop = groups[index]
      const { secondaryMax, primaryField } = groupToPop
      const groupToExpand = groups[index - 1]
      const { secondaryMin } = groupToExpand
      groupToExpand.secondaryMax = secondaryMax
      groupToExpand.archetypeID = `${primaryField}_${secondaryMin}_to_${secondaryMax}`
      groups.splice(index, 1)
    },
    changeArchetypeID: (
      state,
      { payload }: PayloadAction<{ field: string; archetypeID: string; index: number }>,
    ) => {
      handleClearSummaryData(state)
      const { index, field, archetypeID } = payload
      const primaryGroup = state.groups?.find(({ field: _field }) => field === _field)
      // TODO: should throw errors if not found
      if (!primaryGroup) return
      const { groups } = primaryGroup
      const groupToRename = groups[index]
      groupToRename.archetypeID = archetypeID
    },
    changeArchetypeField: (
      state,
      {
        payload,
      }: PayloadAction<{
        archetypeID: string
        field: string
        key: keyof ArchetypeGroup
        value: string | number | boolean | null | undefined
      }>,
    ) => {
      handleClearSummaryData(state)
      const { archetypeID, field, key, value } = payload
      const primaryGroup = state.groups?.find(({ field: _field }) => field === _field)
      if (!primaryGroup) return
      // TODO: should throw errors if not found
      const { groups } = primaryGroup
      const groupToMutate = groups.find(({ archetypeID: id }) => id === archetypeID)
      if (groupToMutate) {
        let x = groupToMutate
        // as casts to prvent ts error
        groupToMutate[key as 'WW'] = value as number
      }
    },
    setContextLayer: (
      state,
      { payload }: PayloadAction<{ layer: keyof ContextLayerConfiguration; value: boolean }>,
    ) => {
      state.contextLayers[payload.layer] = payload.value
    },
    setTemplateLibraryFilters: (
      state,
      { payload }: PayloadAction<{ parameter: keyof TemplatLibraryFilters; values: string[] }>,
    ) => {
      const { parameter, values } = payload
      state.libraryFilters[parameter] = values
    },
    clearUserTemplateAssignments: (
      state,
      { payload }: PayloadAction<{ fromCloud?: boolean; id?: number }>,
    ) => {
      state.groups?.forEach(({ groups }) => {
        groups.forEach((group) => {
          // payload.id is undefined when uploaded outside of project context
          if (group.isUser && (group.cloudLibraryId !== payload.id || payload.id === undefined)) {
            group.isUser = false
            group.template = null
            group.cloudLibraryId = undefined
          }
        })
      })
    },
    clearGroupData: (state) => {
      handleClearGroupData(state)
    },
    clearSummaryData: (state) => {
      handleClearSummaryData(state)
    },
    clearAllData: (state) => {
      handleClearAllData(state)
    },
  },
  extraReducers: (builder) => {
    addErrorExtraReducers(builder, { workflow })
    addAsyncExtraReducers(builder, { workflow, handlers })
  },
})

const addInitialErrorsToGeoJSON = (state: UmibuilderState) => {
  if (state.geoJSON?.features) {
    const errorSummary = { any: 0, overlap: 0 }
    state.geoJSON.features.forEach((feature) => {
      if (feature.properties.UBEM_OVERLAP_ERROR) {
        errorSummary.any += 1
        errorSummary.overlap += 1
      }
    })
    state.keyFieldsErrorSummary = errorSummary
  }
}

type HandleProcessedGisPayload = Pick<
  UmibuilderState,
  'geoJSON' | 'shapefileProps' | 'numericalProps' | 'categoricalProps' | 'fromCloud'
> & {
  mapBounds: [number, number, number, number]
  summaryStats: { polygons: number }
  climateZones: { [key: string]: { CZ: string } }
  buildingCount: number
}
const handleProcessedGis = (
  state: UmibuilderState,
  { payload }: PayloadAction<{ result: HandleProcessedGisPayload }>,
) => {
  const {
    geoJSON,
    shapefileProps,
    numericalProps,
    categoricalProps,
    mapBounds,
    // TODO: Get rid of summary stats and include largeGeoJSON in api response
    summaryStats,
    buildingCount,
    climateZones,
    fromCloud,
  } = payload.result
  if (summaryStats?.polygons > 1000 || (buildingCount ?? 0) > 1000) {
    state.largeGeoJSON = true
    state.geoJSON = {}
  } else {
    state.geoJSON = geoJSON
  }
  addInitialErrorsToGeoJSON(state)
  state.shapefileProps = shapefileProps
  state.categoricalProps = categoricalProps
  state.numericalProps = numericalProps
  state.mapBounds = [
    [mapBounds[1], mapBounds[0]],
    [mapBounds[3], mapBounds[2]],
  ]
  if (climateZones) {
    state.climateZones = Object.values(climateZones).map(({ CZ }) => CZ)
    state.libraryFilters.ClimateZone = [...state.climateZones]
  }
  if (fromCloud) {
    state.fromCloud = true
    // TODO: should be conditional based off of user role
    state.entrypoint = Entrypoint.full
  }
}

const handleFinalArchetypeAssignments = (
  state: UmibuilderState,
  { payload }: PayloadAction<{ result: Summary }>,
) => {
  state.summary = {
    ...payload.result,
  }
  if (!state.summary) return
  state.summary.config = state.summary.config.map((building, i) => ({
    ...building,
    id: i,
  }))
}

const handleUmiReady = (state: UmibuilderState) => {
  state.umiReady = true
}

// Handlers for clearing flags to control page access
const handleClearUmiExport = (state: UmibuilderState) => {
  state.umiReady = false
}

const handleClearSummaryData = (state: UmibuilderState) => {
  state.summary = null
  handleClearUmiExport(state)
}

const handleClearGroupData = (state: UmibuilderState) => {
  state.groups = null
  handleClearSummaryData(state)
}

const handleClearBaseGisData = (state: UmibuilderState) => {
  getEntries(makeBaseGisData()).forEach(([key, value]) => {
    // TODO: deal with polymorphism...
    state[key as 'largeGeoJSON'] = value as boolean
  })
  state.keyFields = makeKeyFields()
  state.fromCloud = false
  handleClearGroupData(state)
}

const handleClearAllData = (state: UmibuilderState) => {
  handleClearBaseGisData(state)
}

// Async Action Handlers
const handlers: TaggedHandlers<UmibuilderState> = {
  [AsyncTask.processBaseGis]: {
    onStart: handleClearBaseGisData,
    onSuccess: handleProcessedGis,
  },
  [AsyncTask.assignBuildingsToArchetypes]: {
    onStart: handleClearSummaryData,
    onSuccess: handleFinalArchetypeAssignments,
  },
  [AsyncTask.startUmiExport]: {
    onStart: handleClearUmiExport,
    onSuccess: handleUmiReady,
  },
}

// Action creators are generated for each case reducer function
export const {
  setEntrypoint,
  setCloudContext,
  setIsProcessing,
  loadGeoJSON,
  setAllKeyFields,
  setKeyFieldAssignment,
  computeErrors,
  loadGroupData,
  addSubdivision,
  removeSubdivision,
  changeArchetypeID,
  changeArchetypeField,
  setContextLayer,
  setTemplateLibraryFilters,
  clearUserTemplateAssignments,
  clearGroupData,
  clearSummaryData,
  clearAllData,
} = umibuilderSlice.actions

export default umibuilderSlice.reducer
